import type { ReactNode } from "react";
import type { RowData } from "@tanstack/react-table";
import type { ViewTableHeader, ViewTableRow } from "./core/table";
import {
    ColumnPinData,
    getGroupHeaderPinData,
    getLiftedHeaderTier,
} from "./ViewTable.lib";
import {
    GroupHeaderCell,
    LeafHeaderCell,
    PlaceholderHeaderCell,
} from "./ViewTableHeaderCells";
import { ViewTableFullWidthRow } from "./ViewTableFullWidthRow";

export function renderFullWidthRowIfPresent<TData extends RowData>({
    row,
    renderFullWidthRow,
    index,
    measureElement,
}: {
    row: ViewTableRow<TData>;
    renderFullWidthRow?: (row: TData) => ReactNode | null;
    index: number;
    measureElement: (node: HTMLElement | null) => void;
}): ReactNode | null {
    if (!renderFullWidthRow) {
        return null;
    }
    const fullWidthContent = renderFullWidthRow(row.original);
    if (fullWidthContent == null) {
        return null;
    }
    return (
        <ViewTableFullWidthRow
            key={row.id}
            content={fullWidthContent}
            index={index}
            measureElement={measureElement}
        />
    );
}

export function renderViewTableHeaderCell<TData extends RowData>({
    header,
    depth,
    headerGroupCount,
    columnPinningMap,
    enableColumnResizing,
    renderHeaderVariantMenu,
    isAllPageRowsSelected,
    isSomePageRowsSelected,
}: {
    header: ViewTableHeader<TData>;
    depth: number;
    headerGroupCount: number;
    columnPinningMap: Map<string, ColumnPinData>;
    enableColumnResizing: boolean;
    renderHeaderVariantMenu: (columnId: string) => ReactNode;
    isAllPageRowsSelected: boolean;
    isSomePageRowsSelected: boolean;
}): ReactNode {
    const pinData = columnPinningMap.get(header.column.id);

    if (header.isPlaceholder) {
        const shouldShowTierLabel =
            getLiftedHeaderTier(
                header.column.columnDef.meta,
                headerGroupCount,
            ) === depth;
        return (
            <PlaceholderHeaderCell
                key={header.id}
                header={header}
                depth={depth}
                pinData={pinData}
                shouldShowTierLabel={shouldShowTierLabel}
            />
        );
    }

    if (header.subHeaders.length > 0) {
        const groupLeafColumnIds = header
            .getLeafHeaders()
            .map((leafHeader) => leafHeader.column.id);
        return (
            <GroupHeaderCell
                key={header.id}
                header={header}
                depth={depth}
                pinData={getGroupHeaderPinData({
                    leafColumnIds: groupLeafColumnIds,
                    columnPinningMap,
                })}
            />
        );
    }

    return (
        <LeafHeaderCell
            key={header.id}
            header={header}
            depth={depth}
            pinData={pinData}
            enableColumnResizing={enableColumnResizing}
            headerVariantMenu={renderHeaderVariantMenu(header.column.id)}
            headerRowCount={headerGroupCount}
            isAllPageRowsSelected={isAllPageRowsSelected}
            isSomePageRowsSelected={isSomePageRowsSelected}
        />
    );
}
